/**
 * Learner CSV parsing — shared by the standalone Bulk Upload page and the
 * course-invitation importer, so both accept exactly the same file.
 *
 * Pure except for PapaParse's file reading: validation, sanitisation and the
 * duplicate check all happen here, and nothing is sent to the API until the
 * caller confirms.
 */
import Papa from 'papaparse';
import DOMPurify from 'dompurify';

export const MAX_ROWS = 1000;
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

export const TEMPLATE_CSV = 'email,first_name,last_name,location,department,role\n';

/** Excel/Sheets treat leading = + - @ as formulas — strip them. */
const stripFormulaChars = (value: string): string => value.replace(/^[=+\-@]/, '');

export interface ValidRow {
  email: string;
  first_name: string;
  last_name: string;
  location?: string;
  department?: string;
  role?: string;
}

export interface RowError {
  row: number;
  email: string;
  error: string;
}

export interface ParsedFile {
  name: string;
  valid: ValidRow[];
  errors: RowError[];
}

/** Reject anything that isn't a CSV under the size cap. Returns an error message, or null. */
export function checkFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return 'Only CSV files are accepted — download the template to get started.';
  }
  if (file.size > MAX_FILE_BYTES) {
    return 'The file is larger than 5 MB.';
  }
  return null;
}

/** Parse + validate a learners CSV: required fields, email shape, duplicates. */
export const parseLearnersCsv = (file: File): Promise<ParsedFile> =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.data.length > MAX_ROWS) {
          reject(new Error(`The file has more than ${MAX_ROWS} rows — please split it.`));
          return;
        }
        const valid: ValidRow[] = [];
        const errors: RowError[] = [];
        const seen = new Set<string>();

        (results.data as Record<string, string>[]).forEach((raw, index) => {
          const rowNumber = index + 2; // header + 1-based
          const email = DOMPurify.sanitize(stripFormulaChars(raw.email || '').trim()).toLowerCase();
          const firstName = DOMPurify.sanitize(stripFormulaChars(raw.first_name || '').trim());
          const lastName = DOMPurify.sanitize(stripFormulaChars(raw.last_name || '').trim());

          if (!email) return errors.push({ row: rowNumber, email: '', error: 'Missing email' });
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
            return errors.push({ row: rowNumber, email, error: 'Invalid email format' });
          if (!firstName) return errors.push({ row: rowNumber, email, error: 'Missing first name' });
          if (!lastName) return errors.push({ row: rowNumber, email, error: 'Missing last name' });
          if (seen.has(email))
            return errors.push({ row: rowNumber, email, error: 'Duplicate email in the file' });

          seen.add(email);
          const row: ValidRow = { email, first_name: firstName, last_name: lastName };
          // Optional attribute columns — names, matched or created server-side.
          for (const key of ['location', 'department', 'role'] as const) {
            const value = DOMPurify.sanitize(stripFormulaChars(raw[key] || '').trim());
            if (value) row[key] = value;
          }
          valid.push(row);
        });

        resolve({ name: file.name, valid, errors });
      },
      error: (error) => reject(new Error(`Could not read the file: ${error.message}`)),
    });
  });

/** Trigger a download of the blank template. */
export function downloadTemplate(): void {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tereiko-learners-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}
